/** @jsx jsx */
import React, { type ComponentType } from 'react';
import { css, jsx } from '@compiled/react';
import { token } from '@atlaskit/tokens';
import { useNudgeStep } from '@atlassian/jira-onboarding-quickstart-core';
import { TimelineAddDateNudgeAsync } from '@atlassian/jira-software-onboarding-nudges--next/src/async';
import { useHeaderState } from '@atlassian/jira-software-roadmap-timeline-table';

type Props = {
	isEmpty: boolean;
	loading: boolean;
	listWidth: number;
	height: number;
	timelineRef?: HTMLElement | null;
};

// Sticky inline nudge component that is positioned on the first row of the table
export const StickyInlineNudge: ComponentType<Props> = ({
	listWidth,
	height,
	loading,
	timelineRef,
	isEmpty,
}: Props) => {
	const [{ headerHeight }] = useHeaderState();
	const [nudgeStep, { setNudgeStep }] = useNudgeStep();

	if (loading || !timelineRef || nudgeStep !== 'timelineAddDateNudge') {
		return null;
	}

	const handleClick = () => {
		setNudgeStep('');
	};

	return (
		<TimelineAddDateNudgeAsync attributes={{ isEmpty }}>
			<Wrapper
				data-testId="business-timeline.ui.timeline-view.sticky-inline-nudge.wrapper"
				width={listWidth}
				height={height}
				offsetTop={headerHeight}
				onClick={isEmpty ? handleClick : undefined}
			/>
		</TimelineAddDateNudgeAsync>
	);
};

const RESIZE_HANDLE_SIZE = 2;

interface WrapperProps {
	width: number;
	offsetTop: number;
	height: number;
	onClick?: () => void;
}

const wrapperBaseStyles = css({
	position: 'relative',
	borderRadius: `0 0 0 ${token('border.radius.200', '8px')}`,
	zIndex: 0,
});

export const Wrapper = ({ width, offsetTop, height, ...rest }: WrapperProps) => (
	<div
		css={wrapperBaseStyles}
		// eslint-disable-next-line jira/react/no-style-attribute
		style={{
			left: `${width - RESIZE_HANDLE_SIZE}px`,

			top: `${offsetTop - 6}px`,
			width: `calc(100% - ${width - RESIZE_HANDLE_SIZE}px)`,
			height: `${height - 3}px`,
		}}
		{...rest}
	/>
);
